<template>
  <component :is="layoutComponent">
    <router-view :key="`${componentKey}`" v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </component>
  <Toast position="top-right" :base-z-index="400">
    <template #closeicon>
      <SvgIcon icon="close" />
    </template>
    <template #messageicon>
      <SvgIcon icon="info" />
    </template>
  </Toast>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import {
  useRoute,
  useRouter,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric,
} from 'vue-router'
import { useScreenStore } from '@/stores/screen'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/order'
import { scrollIntoView } from '@/utils/scroll'
import AuthLayout from '@/layouts/AuthLayout.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import BlankLayout from '@/layouts/BlankLayout.vue'
import Toast from 'primevue/toast'
import {useMutexStore} from "@/stores/mutex.ts";

const route = useRoute()
const router = useRouter()
const screenStore = useScreenStore()
const authStore = useAuthStore()
const orderStore = useOrderStore()
useMutexStore()

const layouts = {
  Auth: AuthLayout,
  Blank: BlankLayout,
  Default: DefaultLayout,
}

const layoutComponent = computed(() => {
  const name = route.meta.layout as keyof typeof layouts
  return layouts[name]
})
const componentKey = computed(() => {
  return route.path
})

const ALLOWED_PAGES_WITHOUT_AUTH = ['login']
// const ADMIN_ROUTES = ['employee']

const isPublicRoute = (routeName: string) => ALLOWED_PAGES_WITHOUT_AUTH.includes(routeName)
// const isAdminRoute = (routeName: string) => ADMIN_ROUTES.includes(routeName)

const handleAuthGuard = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext,
) => {
  const routeName = String(to.name)
  const isAuthorized = authStore.isAuth

  if (isAuthorized && isPublicRoute(routeName)) {
    return next({ path: from.path, replace: true })
  }

  if (!isAuthorized && !isPublicRoute(routeName)) {
    return next({ name: 'login', replace: true })
  }

  // if (authStore.isAdmin && !isAdminRoute(routeName)) {
  //   return next({ name: 'adminCompanies', replace: true })
  // }

  // if (!authStore.isAdmin && isAdminRoute(routeName)) {
  //     return next({ name: 'home', replace: true })
  // }

  return next()
}

const handleRouting = (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext,
) => {
  scrollIntoView(0, 'instant')
  return handleAuthGuard(to, from, next)
}

router.beforeEach(handleRouting)

const setAppWindowWidth = () => (screenStore.windowWidth = window.innerWidth)

watch(
  () => authStore.isAuth,
  async val => {
    if (val) await orderStore.getOrders()
  },
  {
    immediate: true,
  },
)

onMounted(() => {
  window.addEventListener('resize', setAppWindowWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', setAppWindowWidth)
})
</script>

<style lang="scss">
@use '@/assets/styles/main.scss' as *;
</style>
